import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Divider,
  Grid,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LinkIcon from '@mui/icons-material/Link';
import { toast } from 'react-toastify';
import { companiesAPI } from '../../services/api';
import LoadingButton from '../Loading/LoadingButton';

export interface CompanyFormData {
  name: string;
  oldName?: string;
  jurisdiction?: string;
  registrationNumber?: string;
  corporateFilingUrl?: string;
  fiscalYearEndMonth?: number | string | null;
  status?: string;
  address?: string;
  incorporationDate?: string;
  authorisedShareCapitalRaw?: string;
  authorisedShareCapitalValue?: number | string;
  authorisedShareCapitalCurrency?: string;
  numberOfShares?: number | string;
  valuePerShare?: number | string;
  paidUpCapitalRaw?: string;
  paidUpCapitalValue?: number | string;
  numberOfSharesIssued?: number | string;
  managementCompany?: string;
  contactInfo?: string;
  hasNomineeDirectors?: boolean;
  sourceSheet?: string;
  sourceRowNumber?: number | string;
  rawData?: string;
  comments?: string;
}

const FISCAL_YEAR_END_MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export interface CompanyEditDialogProps {
  open: boolean;
  onClose: () => void;
  company?: any; // The company being edited (null for create)
  onSave?: () => void; // Callback after successful save
  jurisdictions?: string[]; // Available jurisdictions for autocomplete
  showAdvancedFields?: boolean; // Show all fields including metadata
}

const CompanyEditDialog: React.FC<CompanyEditDialogProps> = ({
  open,
  onClose,
  company,
  onSave,
  jurisdictions = [],
  showAdvancedFields = false,
}) => {
  const [form, setForm] = useState<CompanyFormData>({
    name: '',
    status: 'ACTIVE',
  });
  const [saving, setSaving] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Initialize form when company changes
  useEffect(() => {
    if (company) {
      setForm({
        name: company.name || '',
        oldName: company.oldName || '',
        jurisdiction: company.jurisdiction || '',
        registrationNumber: company.registrationNumber || '',
        corporateFilingUrl: company.corporateFilingUrl || '',
        fiscalYearEndMonth: company.fiscalYearEndMonth ?? '',
        status: company.status || 'ACTIVE',
        address: company.address || '',
        incorporationDate: company.incorporationDate ? new Date(company.incorporationDate).toISOString().slice(0, 10) : '',
        authorisedShareCapitalRaw: company.authorisedShareCapitalRaw || '',
        authorisedShareCapitalValue: company.authorisedShareCapitalValue ?? '',
        authorisedShareCapitalCurrency: company.authorisedShareCapitalCurrency || '',
        numberOfShares: company.numberOfShares ?? '',
        valuePerShare: company.valuePerShare ?? '',
        paidUpCapitalRaw: company.paidUpCapitalRaw || '',
        paidUpCapitalValue: company.paidUpCapitalValue ?? '',
        numberOfSharesIssued: company.numberOfSharesIssued ?? '',
        managementCompany: company.managementCompany || '',
        contactInfo: company.contactInfo || '',
        hasNomineeDirectors: Boolean(company.hasNomineeDirectors),
        sourceSheet: company.sourceSheet || '',
        sourceRowNumber: company.sourceRowNumber ?? '',
        rawData: company.rawData ? JSON.stringify(company.rawData, null, 2) : '',
        comments: company.comments || '',
      });
    } else {
      // Reset for new company
      setForm({
        name: '',
        status: 'ACTIVE',
        fiscalYearEndMonth: '',
      });
    }
  }, [company, open]);

  const handleChange = (field: keyof CompanyFormData, value: any) => {
    setForm({ ...form, [field]: value });
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Company name is required');
      return;
    }

    setSaving(true);
    try {
      // Convert numeric fields
      const toNum = (v: any) => (v === '' || v === undefined || v === null ? undefined : Number(v));

      const payload: any = {
        ...form,
        authorisedShareCapitalValue: toNum(form.authorisedShareCapitalValue),
        numberOfShares: toNum(form.numberOfShares),
        numberOfSharesIssued: toNum(form.numberOfSharesIssued),
        valuePerShare: toNum(form.valuePerShare),
        paidUpCapitalValue: toNum(form.paidUpCapitalValue),
        sourceRowNumber: toNum(form.sourceRowNumber),
        fiscalYearEndMonth:
          form.fiscalYearEndMonth === '' || form.fiscalYearEndMonth === undefined
            ? null
            : toNum(form.fiscalYearEndMonth),
        hasNomineeDirectors: Boolean(form.hasNomineeDirectors),
      };

      // Remove empty incorporation date
      if (!payload.incorporationDate) {
        delete payload.incorporationDate;
      }

      if (company?.id) {
        // Update existing company
        await companiesAPI.update(company.id, payload);
        toast.success('Company updated successfully');
      } else {
        // Create new company
        await companiesAPI.create(payload);
        toast.success('Company created successfully');
      }

      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to save company:', error);
      toast.error(error.response?.data?.message || 'Failed to save company');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!company?.id) return;

    if (!window.confirm(`Delete company "${company.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await companiesAPI.remove(company.id);
      toast.success('Company deleted successfully');
      if (onSave) {
        onSave();
      }
      onClose();
    } catch (error: any) {
      console.error('Failed to delete company:', error);
      toast.error(error.response?.data?.message || 'Failed to delete company');
    }
  };

  // Smart corporate filing URL suggestions
  const renderCorporateFilingUrlHelper = () => {
    const jurisdictionLower = form.jurisdiction?.toLowerCase() || '';
    const isUK = jurisdictionLower.includes('united kingdom') || jurisdictionLower === 'uk';
    const isIsleOfMan = jurisdictionLower.includes('isle of man') || jurisdictionLower === 'iom';
    const isIndia = jurisdictionLower.includes('india');
    const isCanada = jurisdictionLower.includes('canada');
    const isUAE = jurisdictionLower.includes('united arab emirates') || jurisdictionLower.includes('uae') || jurisdictionLower.includes('dubai') || jurisdictionLower.includes('abu dhabi');
    const isTanzania = jurisdictionLower.includes('tanzania') || jurisdictionLower === 'tz';
    const isKenya = jurisdictionLower.includes('kenya') || jurisdictionLower === 'ke';
    const isUganda = jurisdictionLower.includes('uganda') || jurisdictionLower === 'ug';
    const isMozambique = jurisdictionLower.includes('mozambique') || jurisdictionLower === 'mz';
    const isMalawi = jurisdictionLower.includes('malawi') || jurisdictionLower === 'mw';
    const isDRC = jurisdictionLower.includes('democratic republic of the congo') || jurisdictionLower.includes('congo (drc)') || jurisdictionLower === 'drc' || jurisdictionLower === 'rdc';
    const isZimbabwe = jurisdictionLower.includes('zimbabwe') || jurisdictionLower === 'zw';
    const isAngola = jurisdictionLower.includes('angola') || jurisdictionLower === 'ao';
    const hasRegNum = !!form.registrationNumber;

    if (isUK && hasRegNum) {
      return (
        <Button
          size="small"
          onClick={() => {
            const ukUrl = `https://find-and-update.company-information.service.gov.uk/company/${form.registrationNumber}`;
            handleChange('corporateFilingUrl', ukUrl);
            toast.info('UK Companies House URL suggested');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant={form.corporateFilingUrl ? 'outlined' : 'contained'}
          color={form.corporateFilingUrl ? 'secondary' : 'primary'}
        >
          {form.corporateFilingUrl ? 'Update UK Companies House URL' : 'Suggest UK Companies House URL'}
        </Button>
      );
    }

    if (isIsleOfMan) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://services.gov.im/ded/services/companiesregistry/companysearch.iom', '_blank', 'noopener,noreferrer');
            toast.info('Isle of Man Companies Registry opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open IoM Companies Registry
        </Button>
      );
    }

    if (isIndia) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.mca.gov.in/content/mca/global/en/mca/master-data/MDS.html', '_blank', 'noopener,noreferrer');
            toast.info('India MCA Master Data opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open India MCA Registry
        </Button>
      );
    }

    if (isCanada) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.ic.gc.ca/app/scr/cc/CorporationsCanada/fdrlCrpSrch.html', '_blank', 'noopener,noreferrer');
            toast.info('Corporations Canada Registry opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Corporations Canada Registry
        </Button>
      );
    }

    if (isUAE) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.moec.gov.ae/', '_blank', 'noopener,noreferrer');
            toast.info('UAE Ministry of Economy opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open UAE Ministry of Economy
        </Button>
      );
    }

    if (isTanzania) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://ors.brela.go.tz/', '_blank', 'noopener,noreferrer');
            toast.info('Tanzania BRELA ORS opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Tanzania BRELA ORS
        </Button>
      );
    }

    if (isKenya) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://brs.ecitizen.go.ke/', '_blank', 'noopener,noreferrer');
            toast.info('Kenya BRS (eCitizen) opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Kenya BRS (eCitizen)
        </Button>
      );
    }

    if (isUganda) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://ursb.go.ug/', '_blank', 'noopener,noreferrer');
            toast.info('Uganda URSB opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Uganda URSB
        </Button>
      );
    }

    if (isMozambique) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.portaldogoverno.gov.mz/', '_blank', 'noopener,noreferrer');
            toast.info('Mozambique Government Portal opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Mozambique Government Portal
        </Button>
      );
    }

    if (isMalawi) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.registrargeneral.gov.mw/', '_blank', 'noopener,noreferrer');
            toast.info('Malawi Registrar General opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Malawi Registrar General
        </Button>
      );
    }

    if (isDRC) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.guichetunique.cd/', '_blank', 'noopener,noreferrer');
            toast.info('DRC Guichet Unique opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open DRC Guichet Unique
        </Button>
      );
    }

    if (isZimbabwe) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://dcip.gov.zw/', '_blank', 'noopener,noreferrer');
            toast.info('Zimbabwe DCIP portal opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Zimbabwe DCIP
        </Button>
      );
    }

    if (isAngola) {
      return (
        <Button
          size="small"
          onClick={() => {
            window.open('https://www.gue.gov.ao/', '_blank', 'noopener,noreferrer');
            toast.info('Angola GUE opened. Search for the company and paste the URL here.');
          }}
          sx={{ mt: 1 }}
          startIcon={<LinkIcon />}
          variant="outlined"
        >
          Open Angola GUE
        </Button>
      );
    }

    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={isMobile}>
      <DialogTitle sx={isMobile ? { px: 2, py: 1.5 } : undefined}>
        {company ? 'Edit Company' : 'Create Company'}
      </DialogTitle>
      <DialogContent sx={isMobile ? { px: 2 } : undefined}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Basic Information */}
          <TextField
            label="Company Name *"
            value={form.name || ''}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
            autoFocus
          />

          <TextField
            label="Old Name"
            value={form.oldName || ''}
            onChange={(e) => handleChange('oldName', e.target.value)}
            fullWidth
            helperText="Previous company name if renamed"
          />

          <Autocomplete
            options={jurisdictions}
            value={form.jurisdiction || null}
            onChange={(e, value) => handleChange('jurisdiction', value || '')}
            renderInput={(params) => <TextField {...params} label="Jurisdiction" />}
            freeSolo
          />

          <Select
            value={form.status || 'ACTIVE'}
            onChange={(e) => handleChange('status', e.target.value)}
            displayEmpty
            fullWidth
          >
            <MenuItem value="ACTIVE">ACTIVE</MenuItem>
            <MenuItem value="WOUND_UP">WOUND_UP</MenuItem>
            <MenuItem value="DISSOLVED">DISSOLVED</MenuItem>
            <MenuItem value="INACTIVE">INACTIVE</MenuItem>
          </Select>

          <TextField
            select
            label="Fiscal Year End Month"
            value={form.fiscalYearEndMonth ?? ''}
            onChange={(e) =>
              handleChange(
                'fiscalYearEndMonth',
                e.target.value === '' ? '' : Number(e.target.value),
              )
            }
            fullWidth
            helperText="Used for fiscal-year statement due dates. Leave unset to default to Dec 31."
          >
            <MenuItem value="">Not set (default Dec 31)</MenuItem>
            {FISCAL_YEAR_END_MONTH_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Registration Number"
            value={form.registrationNumber || ''}
            onChange={(e) => handleChange('registrationNumber', e.target.value)}
            fullWidth
          />

          <Box>
            <TextField
              label="Corporate Filing URL"
              value={form.corporateFilingUrl || ''}
              onChange={(e) => handleChange('corporateFilingUrl', e.target.value)}
              fullWidth
              placeholder="https://..."
              helperText="URL to official company registry page"
            />
            {renderCorporateFilingUrlHelper()}
          </Box>

          <TextField
            label="Incorporation Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={form.incorporationDate || ''}
            onChange={(e) => handleChange('incorporationDate', e.target.value)}
            fullWidth
          />

          <TextField
            label="Address"
            value={form.address || ''}
            onChange={(e) => handleChange('address', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          <Divider sx={{ my: 1 }} />

          {/* Share Capital Section */}
          <Typography variant="subtitle2" color="text.secondary">
            Share Capital
          </Typography>

          <TextField
            label="Authorised Share Capital (Raw)"
            value={form.authorisedShareCapitalRaw || ''}
            onChange={(e) => handleChange('authorisedShareCapitalRaw', e.target.value)}
            fullWidth
            placeholder="e.g., 100,000,000 TZS"
            helperText="Original text as it appears in documents"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Auth. Share Capital (Value)"
                type="number"
                value={form.authorisedShareCapitalValue || ''}
                onChange={(e) => handleChange('authorisedShareCapitalValue', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Currency"
                value={form.authorisedShareCapitalCurrency || ''}
                onChange={(e) => handleChange('authorisedShareCapitalCurrency', e.target.value)}
                fullWidth
                placeholder="USD, GBP, TZS, etc."
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Shares"
                type="number"
                value={form.numberOfShares || ''}
                onChange={(e) => handleChange('numberOfShares', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Value per Share"
                type="number"
                value={form.valuePerShare || ''}
                onChange={(e) => handleChange('valuePerShare', e.target.value)}
                fullWidth
                inputProps={{ step: '0.01' }}
              />
            </Grid>
          </Grid>

          <TextField
            label="Paid Up Capital (Raw)"
            value={form.paidUpCapitalRaw || ''}
            onChange={(e) => handleChange('paidUpCapitalRaw', e.target.value)}
            fullWidth
            placeholder="e.g., 50,000,000 TZS"
            helperText="Original text as it appears in documents"
          />

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Paid Up Capital (Value)"
                type="number"
                value={form.paidUpCapitalValue || ''}
                onChange={(e) => handleChange('paidUpCapitalValue', e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Number of Shares Issued"
                type="number"
                value={form.numberOfSharesIssued || ''}
                onChange={(e) => handleChange('numberOfSharesIssued', e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>

          {showAdvancedFields && (
            <>
              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Management & Contact
              </Typography>

              <TextField
                label="Management Company"
                value={form.managementCompany || ''}
                onChange={(e) => handleChange('managementCompany', e.target.value)}
                fullWidth
              />

              <TextField
                label="Contact Info"
                value={form.contactInfo || ''}
                onChange={(e) => handleChange('contactInfo', e.target.value)}
                fullWidth
                placeholder="Email, phone, or managing director name"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={Boolean(form.hasNomineeDirectors)}
                    onChange={(e) => handleChange('hasNomineeDirectors', e.target.checked)}
                  />
                }
                label="Has Nominee Directors"
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Data Source Metadata
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Source Sheet"
                    value={form.sourceSheet || ''}
                    onChange={(e) => handleChange('sourceSheet', e.target.value)}
                    fullWidth
                    placeholder="e.g., Tanzania, UK, BVI"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Source Row Number"
                    type="number"
                    value={form.sourceRowNumber || ''}
                    onChange={(e) => handleChange('sourceRowNumber', e.target.value)}
                    fullWidth
                  />
                </Grid>
              </Grid>

              <TextField
                label="Raw Data (JSON)"
                value={form.rawData || ''}
                onChange={(e) => handleChange('rawData', e.target.value)}
                multiline
                minRows={3}
                fullWidth
                helperText="Original data as imported from source file"
              />
            </>
          )}

          <TextField
            label="Comments"
            value={form.comments || ''}
            onChange={(e) => handleChange('comments', e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          ...(isMobile && {
            flexDirection: 'column',
            gap: 1,
            px: 2,
            pb: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
            '& > .MuiButton-root, & > .MuiLoadingButton-root': {
              width: '100%',
              minHeight: 44,
            },
          }),
        }}
      >
        {company?.id && (
          <Button color="error" onClick={handleDelete}>
            Delete
          </Button>
        )}
        {!isMobile && <Box sx={{ flexGrow: 1 }} />}
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <LoadingButton loading={saving} variant="contained" onClick={handleSave} disabled={!form.name}>
          {company ? 'Save' : 'Create'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyEditDialog;
